import { useCallback, useMemo } from 'react';

import { useTranslation } from '@/i18n';

import { monthlyCutsService } from '@/api/domains/attendance';
import type { MonthlyCutsExportParams } from '@/api/domains/attendance/services/monthlyCuts.service';
import { useExport } from '@/hooks/ui/useExport';
import type { PaginatedListSort } from '@/hooks/ui/usePaginatedList';
import type { UIExportFormat } from '@/types/fileExport.types';


export interface UseMonthlyCutsExportParams {
  searchText: string;
  sort: PaginatedListSort | undefined;
  filterCompanyId?: string | null;
  year?: string | null;
  month?: string | null;
  state?: string | null;
}

export interface UseMonthlyCutsExportResult {
  onExport: (format: UIExportFormat) => Promise<void>;
  isExporting: boolean;
}

export function useMonthlyCutsExport({
  searchText,
  sort,
  filterCompanyId,
  year,
  month,
  state,
}: UseMonthlyCutsExportParams): UseMonthlyCutsExportResult {
  const { t: translate } = useTranslation();

  const exportParams = useMemo<MonthlyCutsExportParams>(
    () => ({
      searchText: searchText || undefined,
      sort,
      filterCompanyId: filterCompanyId ?? undefined,
      year: year ?? undefined,
      month: month ?? undefined,
      state: state ?? undefined,
    }),
    [searchText, sort, filterCompanyId, year, month, state],
  );

  const { onExport: exportFile, isExporting } = useExport({
    exportFn: () => monthlyCutsService.exportFile(exportParams),
    successMessage: translate('attendance:monthlyCutsList.messages.exportSuccess'),
    errorMessage: translate('attendance:monthlyCutsList.messages.exportError'),
  });

  const onExport = useCallback(async (_format: UIExportFormat) => {
    await exportFile();
  }, [exportFile]);

  return { onExport, isExporting };
}
